import { useEffect, useRef } from 'react';
import { hasManualTradingOrigin } from '@/config/api';
import {
  parseTradingWsMessage,
  tradingEventsWsUrl,
  type TradingEvent,
} from '@/services/tradingEventsApi';

const RECONNECT_MS = 3000;

export function useTradingEvents(onEvent: (event: TradingEvent) => void) {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!hasManualTradingOrigin()) return undefined;

    let ws: WebSocket | null = null;
    let disposed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (disposed) return;
      const url = tradingEventsWsUrl();
      if (!url) return;

      ws = new WebSocket(url);

      ws.onmessage = (evt) => {
        if (typeof evt.data !== 'string') return;
        const msg = parseTradingWsMessage(evt.data);
        if (!msg || msg.type !== 'event') return;
        onEventRef.current(msg);
      };

      ws.onclose = () => {
        if (!disposed) {
          reconnectTimer = setTimeout(connect, RECONNECT_MS);
        }
      };
    };

    connect();

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, []);
}
